import {UserOutlined} from '@ant-design/icons';
import {Button, Col, Form, Input, InputNumber, Modal, Row, Select, Switch} from 'antd';
import PropTypes from 'prop-types';
import {useEffect} from 'react';

const {Option} = Select;

const SectionLabel = ({children}) => (
  <p className={'text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3 mt-1'}>
    {children}
  </p>
);
SectionLabel.propTypes = {children: PropTypes.node.isRequired};

const EditEmployeeScreen = ({
  open,
  employee,
  token,
  loggedInRole,
  onSave,
  isUpdating,
  onClose,
}) => {
  const [form] = Form.useForm();

  const role = employee?.roles;

  const isEmployee = role === 'EMPLOYEE';

  const isCustomerOrVendor =
    role === 'CUSTOMER' || role === 'VENDOR';

  useEffect(() => {
    if (employee && open) {
      form.setFieldsValue({
        firstName: employee.name?.split(' ')[0] || employee.firstName || '',
        lastName:
    employee.name?.split(' ').slice(1).join(' ') ||
    employee.lastName ||
    '',
        email: employee.email || '',
        mobile: employee.mobile || '',
        address: employee.address || '',
        street: employee.street || '',
        apartment: employee.apartment || '',
        pinCode: employee.pinCode || '',
        city: employee.city || '',
        district: employee.district || '',
        state: employee.state || '',
        status: employee.status || 'ACTIVE',

        perDaysWages: employee.perDaysWages ?? 0,
        wagesRate: employee.wagesRate ?? 0,
        verifcation: employee.verifcation ?? false,

        businessName: employee.businessName || '',
        ldnumber: employee.ldnumber || '',
        gstIn: employee.gstIn || '',
      });
    }
  }, [employee, open, form]);

  const noWhitespace = {
    validator: (_, value) =>
      value && value.trim() === ''
        ? Promise.reject('Cannot be empty')
        : Promise.resolve(),
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {id: employee.id, token, data: values};
      if (loggedInRole === 'ADMIN') {
        payload.role = employee.roles;
      }
      await onSave(payload);
      onClose();
    } catch {
    //a
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={720}
      title={(
        <div className={'flex items-center gap-2'}>
          <UserOutlined className={'text-slate-600'} />
          <span className={'font-bold text-slate-800'}>Edit Employee</span>
        </div>
      )}
      footer={[
        <Button key={'cancel'} onClick={onClose}>Cancel</Button>,
        <Button
          key={'save'}
          type={'primary'}
          loading={isUpdating}
          onClick={handleSubmit}
          className={'btn-primary'}
        >
          Save Changes
        </Button>,
      ]}
    >
      <Form form={form} layout={'vertical'} scrollToFirstError className={'mt-2'}>

        <SectionLabel>Personal Information</SectionLabel>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item label={'First Name'} name={'firstName'} rules={[{required: true, message: 'First Name is Required'}]}>
              <Input placeholder={'John'} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label={'Last Name'} name={'lastName'} rules={[{required: true, message: 'Last Name is Required'}]}>
              <Input placeholder={'Doe'} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              label={'Email'} name={'email'}
              rules={[{type: 'email', message: 'Invalid email'}]}
            >
              <Input placeholder={'john.doe@example.com'} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label={'Status'} name={'status'} rules={[{required: true}]}>
              <Select>
                <Option value={'ACTIVE'}>Active</Option>
                <Option value={'INACTIVE'}>Inactive</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          className={'mb-6'}
          label={'Mobile'} name={'mobile'}
          rules={[
            {required: true, message: 'Required'},
            {pattern: /^[0-9]{10}$/, message: 'Enter valid 10 digit mobile number'},
          ]}
        >
          <Input
            placeholder={'9876543210'}
            maxLength={10}
            onInput={e => { e.target.value = e.target.value.replace(/\D/g, ''); }}
          />
        </Form.Item>

        <SectionLabel>Address</SectionLabel>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item label={'Address'} name={'address'}><Input placeholder={'Full address'} /></Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label={'Street'} name={'street'}><Input placeholder={'Street'} /></Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item label={'Apartment'} name={'apartment'}><Input placeholder={'Apt / Suite / Floor'} /></Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label={'Pin Code'} name={'pinCode'}><Input placeholder={'400001'} maxLength={6} /></Form.Item>
          </Col>
        </Row>

        {isEmployee && (
          <>
            <Row gutter={16}>
              <Col xs={24} sm={8}>
                <Form.Item
                  label={'Per Day Wages'}
                  name={'perDaysWages'}
                  rules={[{required: true, message: 'Required'}]}
                >
                  <InputNumber min={0} step={0.01} className={'w-full'} placeholder={'0'} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={8}>
                <Form.Item
                  label={'Wages Rate'}
                  name={'wagesRate'}
                  rules={[{required: true, message: 'Required'}]}
                >
                  <InputNumber min={0} step={0.01} className={'w-full'} placeholder={'0'} />
                </Form.Item>
              </Col>
              
            </Row>
          </>
        )}
        {isCustomerOrVendor && (
          <>
            <SectionLabel>Business Information</SectionLabel>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label={'Business Name'}
                  name={'businessName'}
                >
                  <Input placeholder={'Business Name'} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  label={'GSTIN'}
                  name={'gstIn'}
                >
                  <Input
                    placeholder={'27ABCDE1234F1Z5'}
                    maxLength={15}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label={'LD Number'} name={'ldnumber'}
                  rules={[noWhitespace]}
                >
                  <Input
                    placeholder={'LD123456'}
                    onChange={e => form.setFieldValue('ldnumber', e.target.value.toUpperCase())}
                  />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}        
      </Form>
    </Modal>
  );
};

EditEmployeeScreen.propTypes = {
  open:        PropTypes.bool,
  employee:    PropTypes.object,
  token:       PropTypes.string,
  loggedInRole: PropTypes.string,
  onSave:      PropTypes.func.isRequired,   
  isUpdating:  PropTypes.bool,
  onClose:     PropTypes.func,
};

export default EditEmployeeScreen;
